import * as R from 'ramda'
import { computed, ComputedRef, ref, watch } from 'vue'

import { Confirmation, Participant } from '@collector/sportsapi-client-legacy'
import { Incident } from '@collector/sportsapi-types'
import { useIncidents } from '@desktop/views/Relation/composables'
import { useIncidentName } from '@desktop/views/Relation/Sports/composables'

export function useCanAddOneTbd(
  name: string | undefined,
  incidentId: Incident['id'],
  participantId: Participant['id'],
): {
  disabled: ComputedRef<boolean>
  disabledTooltipText: ComputedRef<string | undefined>
  setUuid(value: string): void
  setParticipantId(participantId: Participant['id']): void
} {
  const { getIncidentName } = useIncidentName()
  const { incidentsUpdate, incidents } = useIncidents()

  // state
  const uuid = ref<string>()
  const id = ref<Incident['id']>()

  // computed
  const disabled = computed(() => !!uuid.value || !!id.value)
  const disabledTooltipText = computed(() =>
    disabled.value ? getDisabledTooltipText() : undefined,
  )

  // methods
  function getDisabledTooltipText(): string | undefined {
    const incidentName = name || getIncidentName(incidentId)

    return `Confirm or cancel previous ${incidentName}`
  }

  function setUuid(value: string): void {
    uuid.value = value
  }

  function setParticipantId(participantId: Participant['id']): void {
    uuid.value = undefined
    init(participantId)
  }

  function init(participantId: Participant['id']): void {
    const incident = R.findLast(
      (incident: Incident) =>
        incident.confirmation === Confirmation.ToBeDone &&
        incident.incident_id === incidentId &&
        incident.participant_team_id === participantId,
    )(incidents.value)

    if (incident) {
      id.value = incident.id
    } else {
      id.value = undefined
    }
  }

  // init
  init(participantId)

  watch(incidentsUpdate, (newVal) => {
    for (const incidentUpdate of newVal) {
      switch (incidentUpdate.data.action) {
        case 'insert':
          if (incidentUpdate.uuid === uuid.value) {
            id.value = incidentUpdate.data.id
          }
          break

        case 'update':
          if (
            incidentUpdate.data.id === id.value &&
            [Confirmation.Confirmed, Confirmation.Cancelled].includes(
              incidentUpdate.data.confirmation,
            )
          ) {
            uuid.value = undefined
            id.value = undefined
          }
          break

        default:
          break
      }
    }
  })

  return {
    disabled,
    disabledTooltipText,
    setUuid,
    setParticipantId,
  }
}
